import { useCallback } from 'react';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { Button } from '@cbhq/cds-web/buttons/Button';

// CDS lays the banner body out as a row from tablet width up, which puts the
// action in a narrow column beside the text. The design has it below the text,
// full width.
const BANNER_STYLES = { body: { flexDirection: 'column' as const } };

export type GracePeriodNoticeAction = {
  label: string;
  onPress: () => void;
  loading?: boolean;
};

export type GracePeriodNoticeProps = {
  /** Bold first line, e.g. "Newly granted Admin access starts Sep 27, 10:00 AM PDT". */
  title: string;
  body: string;
  /** Muted line under the body. */
  meta?: string;
  action?: GracePeriodNoticeAction;
  testID?: string;
};

/**
 * Shared notice for the admin grace period, used by `PendingRoleBanner` and the
 * protected-role warning in the invite and edit trays. Built on the CDS warning
 * `Banner` with a shield icon.
 */
export function GracePeriodNotice({
  title,
  body,
  meta,
  action,
  testID = 'grace-period-notice',
}: GracePeriodNoticeProps) {
  const handleActionPress = useCallback(() => action?.onPress(), [action]);

  return (
    <Banner
      variant="warning"
      styleVariant="contextual"
      startIcon="shieldOutline"
      // CDS only draws a border for the global style variant; the prototype has
      // a subtle outline on the contextual notice.
      bordered
      borderColor="bgLine"
      title={title}
      label={meta}
      primaryAction={
        action ? (
          <Button
            variant="secondary"
            compact
            block
            loading={action.loading}
            onClick={handleActionPress}
          >
            {action.label}
          </Button>
        ) : undefined
      }
      width="100%"
      styles={BANNER_STYLES}
      testID={testID}
    >
      {body}
    </Banner>
  );
}
